import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Trophy, LifeBuoy, BarChart3, Shield, ShieldCheck, Settings, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function Tile({ icon: Icon, label, testId, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-2 py-4 text-center transition-all active:scale-95 hover:bg-white/[0.06]"
    >
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/[0.04] text-zinc-300 transition-colors group-hover:bg-[#c8ff00]/12 group-hover:text-[#c8ff00]">
        <Icon size={20} strokeWidth={1.9} />
      </span>
      <span className="text-[12px] font-medium leading-tight text-zinc-300">
        {label}
      </span>
    </button>
  );
}

export function MoreSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();

  const adminPages = ["dashboard", "submissions", "socials", "users", "wallet", "payouts", "analytics", "roles", "contact", "announcements"];
  const adminPerms = ["view_dashboard", "view_analytics", "review_submissions", "manage_payouts", "manage_roles", "view_creator_insights"];
  const modPages = ["mod_dashboard", "mod_submissions", "mod_socials", "mod_users", "mod_wallet", "mod_templates", "mod_bulk", "mod_announcements"];
  const modPerms = ["review_submissions", "manage_payouts"];
  const canAdminSection = hasAccess(adminPages, adminPerms);
  const showModSection = hasAccess(modPages, modPerms);

  function go(path) {
    onOpenChange?.(false);
    navigate(path);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onOpenChange?.(false);
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const bottomOffset = "calc(env(safe-area-inset-bottom, 0px) + 92px)";

  return (
    <div className="fixed inset-0 z-[55] pointer-events-none">
      <button
        data-testid="mobile-more-backdrop"
        aria-label="Close more menu"
        onClick={() => onOpenChange?.(false)}
        className="pointer-events-auto absolute inset-0 bg-black/40 backdrop-blur-[3px] animate-[more-fade_180ms_ease-out_both]"
      />

      <div
        data-testid="mobile-more-sheet"
        role="dialog"
        aria-modal="true"
        className={[
          "pointer-events-auto absolute left-3 right-3",
          "rounded-3xl border border-white/[0.06] bg-[#0f0f0f]/98 p-4 backdrop-blur-2xl",
          "shadow-[0_20px_60px_-8px_rgba(0,0,0,0.7)]",
          "animate-[more-pop_220ms_cubic-bezier(0.22,1,0.36,1)_both]",
        ].join(" ")}
        style={{ bottom: bottomOffset }}
      >
        {/* Header row */}
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[13px] font-semibold text-white">More</div>
          <button
            type="button"
            data-testid="mobile-more-close"
            onClick={() => onOpenChange?.(false)}
            className="grid h-7 w-7 place-items-center rounded-full text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        {/* Creator tiles */}
        <div className="grid grid-cols-4 gap-2">
          <Tile icon={Star} label="My Campaigns" testId="more-my-campaigns" onClick={() => go("/my-campaigns")} />
          <Tile icon={Trophy} label="Leaderboard" testId="more-leaderboard" onClick={() => go("/leaderboard")} />
          <Tile icon={LifeBuoy} label="Support" testId="more-support" onClick={() => go("/help")} />
          <Tile icon={BarChart3} label="Insights" testId="more-insights" onClick={() => go("/insights")} />
        </div>

        {/* Staff tiles */}
        {(canAdminSection || showModSection) && (
          <>
            <div className="mt-3 mb-2 h-px bg-white/[0.06]" />
            <div className="grid grid-cols-4 gap-2">
              {showModSection && (
                <Tile icon={ShieldCheck} label="Moderator" testId="more-mod" onClick={() => go("/mod")} />
              )}
              {canAdminSection && (
                <Tile icon={Shield} label="Admin" testId="more-admin" onClick={() => go("/admin")} />
              )}
              <Tile icon={Settings} label="Settings" testId="more-settings" onClick={() => go("/settings")} />
            </div>
          </>
        )}

        {/* If no staff, still show Settings in a bottom row */}
        {!canAdminSection && !showModSection && (
          <>
            <div className="mt-3 mb-2 h-px bg-white/[0.06]" />
            <div className="grid grid-cols-4 gap-2">
              <Tile icon={Settings} label="Settings" testId="more-settings" onClick={() => go("/settings")} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
